import React, { Suspense, lazy } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { Context } from './context';
import { notification, Spin } from 'antd';
import ContentLayout from './hoc/ContentLayout';
import PrivateRoute from './components/PrivateRoute';
import { 
  TableSkeleton, 
  FeedSkeleton, 
  TaskDetailSkeleton, 
  FormSkeleton, 
  CalendarSkeleton,
  WeatherSkeleton,
  ReportSkeleton,
  UserJournalSkeleton,
  InstructionsSkeleton,
  ProcessSkeleton,
} from './components/Skeletons';
import { cleanErrorMessage } from './helpers/errorFormatter';
import './App.css';

// Higher-order component to wrap each route in its own dedicated skeleton
const withSkeleton = (Component, Fallback) => (props) => (
  <Suspense fallback={<Fallback />}>
    <Component {...props} />
  </Suspense>
);

// Lazy-loaded page components for Code Splitting
const Authorization = lazy(() => import('./pages/Authorization'));
const Main = lazy(() => import('./pages/Main'));
const UserJournal = lazy(() => import('./pages/UserJournal'));
const WorkTypeJournal = lazy(() => import('./pages/WorkTypeJournal'));
const ProcessJournal = lazy(() => import('./pages/New_work_type/ProcessJournal'));
const EditWorkTypeJournal = lazy(() => import('./pages/EditWorkTypeJournal'));
const WeatherJournal = lazy(() => import('./pages/WeatherJournal'));
const ElectroJournal = lazy(() => import('./pages/ElectroJournal'));
const ElectroTask = lazy(() => import('./pages/ElectroTask'));
const NonElectroJournal = lazy(() => import('./pages/NonElectroJournal'));
const NonElectroTask = lazy(() => import('./pages/NonElectroTask'));
const Operation = lazy(() => import('./pages/Operation'));
const CurrentOperation = lazy(() => import('./pages/CurrentOperation'));
const PPRCalendar = lazy(() => import('./pages/PPR/index_main'));
const PPRTable = lazy(() => import('./pages/PPR/Table'));
const CreateTask = lazy(() => import('./pages/CreateTask'));
const New_CreateTask = lazy(() => import('./pages/CreateTask/index_new'));
const Report = lazy(() => import('./pages/Report'));
const TechnologyJournal = lazy(() => import('./pages/New_work_type/TechnololyJournal'));
const ZonesJournal = lazy(() => import('./pages/New_work_type/ZonesJournal'));
const ConditionJournal = lazy(() => import('./pages/New_work_type/ConditionJournal'));
const NewTaskTypeJournal = lazy(() => import('./pages/New_work_type/NewTaskTypeJournal'));
const Smax = lazy(() => import('./pages/Smax'));
const SmaxJournal = lazy(() => import('./pages/SmaxJournal'));
const TaskTypeOperationsJournal = lazy(() => import('./pages/New_work_type/OperationsJournal'));
const Instructions = lazy(() => import('./pages/Instructions'));

// Specialized Wrapped Route Components
const AuthorizationRoute = withSkeleton(Authorization, () => <FormSkeleton fields={4} columns={1} />);
const MainRoute = withSkeleton(Main, FeedSkeleton);
const UserJournalRoute = withSkeleton(UserJournal, UserJournalSkeleton);
const WorkTypeJournalRoute = withSkeleton(WorkTypeJournal, () => <ProcessSkeleton title="Справочник" />);
const EditWorkTypeJournalRoute = withSkeleton(EditWorkTypeJournal, () => <ProcessSkeleton title="Редактировать справочник" hasBreadcrumbs={true} />);
const WeatherJournalRoute = withSkeleton(WeatherJournal, WeatherSkeleton);
const ElectroJournalRoute = withSkeleton(ElectroJournal, () => <TableSkeleton hasFilters={true} hasSearch={true} />);
const ElectroTaskRoute = withSkeleton(ElectroTask, TaskDetailSkeleton);
const NonElectroJournalRoute = withSkeleton(NonElectroJournal, () => <TableSkeleton hasFilters={true} hasSearch={true} />);
const NonElectroTaskRoute = withSkeleton(NonElectroTask, TaskDetailSkeleton);
const OperationRoute = withSkeleton(Operation, () => <TableSkeleton hasFilters={true} hasSearch={true} />);
const OperationDetailRoute = withSkeleton(Operation, TaskDetailSkeleton);
const CurrentOperationRoute = withSkeleton(CurrentOperation, () => <TableSkeleton hasFilters={true} hasSearch={true} />);
const CurrentOperationDetailRoute = withSkeleton(CurrentOperation, TaskDetailSkeleton);
const NewCreateTaskRoute = withSkeleton(New_CreateTask, () => <FormSkeleton fields={10} columns={2} />);
const CreateTaskRoute = withSkeleton(CreateTask, () => <FormSkeleton fields={8} columns={2} />);
const ProcessJournalRoute = withSkeleton(ProcessJournal, () => <ProcessSkeleton title="Виды процессов" />);
const TechnologyJournalRoute = withSkeleton(TechnologyJournal, () => <ProcessSkeleton title="Объекты процесса" hasBreadcrumbs={true} />);
const ZonesJournalRoute = withSkeleton(ZonesJournal, () => <ProcessSkeleton title="Зоны обслуживания" hasBreadcrumbs={true} />);
const ConditionJournalRoute = withSkeleton(ConditionJournal, () => <ProcessSkeleton title="Условия" hasBreadcrumbs={true} />);
const NewTaskTypeJournalRoute = withSkeleton(NewTaskTypeJournal, () => <ProcessSkeleton title="Виды работ" hasBreadcrumbs={true} />);
const TaskTypeOperationsJournalRoute = withSkeleton(TaskTypeOperationsJournal, () => <ProcessSkeleton title="Список операций" hasBreadcrumbs={true} />);
const InstructionsRoute = withSkeleton(Instructions, InstructionsSkeleton);
const ReportRoute = withSkeleton(Report, ReportSkeleton);
const SmaxRoute = withSkeleton(Smax, () => <FormSkeleton fields={6} columns={2} />);
const SmaxJournalRoute = withSkeleton(SmaxJournal, () => <TableSkeleton hasFilters={false} hasSearch={true} />);
const PPRCalendarRoute = withSkeleton(PPRCalendar, CalendarSkeleton);
const PPRTableRoute = withSkeleton(PPRTable, CalendarSkeleton);

function App(props) {
  const toNotify = (type = 'info', message = '', description = '') => {
    let cleanMsg = message;
    let cleanDesc = description;
    if (type === 'error') {
      cleanMsg = cleanErrorMessage(message) || 'Ошибка';
      cleanDesc = cleanErrorMessage(description);
      if (!cleanDesc && cleanMsg) {
        cleanDesc = cleanMsg;
        cleanMsg = 'Ошибка';
      }
    }
    notification[type]({
      message: cleanMsg,
      description: cleanDesc,
    });
  };

  return (
    <Context.Provider value={{ toNotify }}>
      <ContentLayout isAuthenticated={props.isAuthenticated}>
        <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><Spin size="large" /></div>}>
          <Switch>
            <Route path='/' component={AuthorizationRoute} exact />
            <PrivateRoute path='/main' component={MainRoute} />
            <PrivateRoute path='/user-journal' component={UserJournalRoute} />
            <PrivateRoute
              path='/work-type-journal'
              exact
              component={WorkTypeJournalRoute}
            />
            <PrivateRoute
              path='/work-type-journal/:id'
              exact
              component={EditWorkTypeJournalRoute}
            />
            <PrivateRoute path='/weather-journal' component={WeatherJournalRoute} />
            <PrivateRoute
              path='/electro-journal'
              exact
              component={ElectroJournalRoute}
            />
            <PrivateRoute
              path='/electro-journal/:id'
              exact
              component={ElectroTaskRoute}
            />
            <PrivateRoute
              path='/non-electro-journal'
              exact
              component={NonElectroJournalRoute}
            />
            <PrivateRoute
              path='/non-electro-journal/:id'
              exact
              component={NonElectroTaskRoute}
            />
            <PrivateRoute path='/operation' exact component={OperationRoute} />
            <PrivateRoute path='/operation/id=:id' exact component={OperationDetailRoute} />
            <PrivateRoute
              path='/current-operation'
              exact
              component={CurrentOperationRoute}
            />
            <PrivateRoute
              path='/current-operation/id=:id'
              exact
              component={CurrentOperationDetailRoute}
            />
            <PrivateRoute
              path='/create-task-new'
              exact
              component={NewCreateTaskRoute}
            />
            <PrivateRoute
              path='/ProcessJournal'
              exact
              component={ProcessJournalRoute}
            />
            <PrivateRoute
              path='/TechnologyJournal/:id'
              exact
              component={TechnologyJournalRoute}
            />
            <PrivateRoute
              path='/TechnologyJournal'
              exact
              component={TechnologyJournalRoute}
            />
            <PrivateRoute
              path='/ZonesJournal/:id'
              exact
              component={ZonesJournalRoute}
            />
            <PrivateRoute
              path='/ZonesJournal'
              exact
              component={ZonesJournalRoute}
            />
            <PrivateRoute
              path='/ConditionJournal/:id'
              exact
              component={ConditionJournalRoute}
            />
            <PrivateRoute
              path='/ConditionJournal'
              exact
              component={ConditionJournalRoute}
            />
            <PrivateRoute
              path='/NewTaskTypeJournal/:id'
              exact
              component={NewTaskTypeJournalRoute}
            />
            <PrivateRoute
              path='/instructions/'
              exact
              component={InstructionsRoute}
            />
            <PrivateRoute
              path='/instructions/:id'
              exact
              component={InstructionsRoute}
            />
            <PrivateRoute
              path='/TaskTypeOperationsJournal/:id'
              exact
              component={TaskTypeOperationsJournalRoute}
            />
            <PrivateRoute
              path='/NewTaskTypeJournal'
              exact
              component={NewTaskTypeJournalRoute}
            />
            <PrivateRoute path='/create-task' exact component={CreateTaskRoute} />
            <PrivateRoute path='/report' exact component={ReportRoute} />
            <PrivateRoute path='/smax' exact component={SmaxRoute}/>
            <PrivateRoute path='/smax_journal' exact component={SmaxJournalRoute}/>
            <PrivateRoute path='/smax_journal/:id' exact component={SmaxJournalRoute}/>
            <PrivateRoute path='/calendar/:id' exact component={PPRCalendarRoute} />
            <PrivateRoute path='/ppr_table' exact component={PPRTableRoute}/>
            <Redirect to={props.isAuthenticated ? '/main' : '/'} />
          </Switch>
        </Suspense>
      </ContentLayout>
    </Context.Provider>
  );
}

function mapStateToProps(state) {
  return {
    isAuthenticated: state.auth.isAuthenticated,
  };
}

export default connect(mapStateToProps)(App);
